/**
 * Deeplink Technology Utility
 * Automatically opens links in Safari/Chrome for better user experience
 */

/**
 * Detects if the user is on iOS
 * @returns {boolean}
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Detects if the user is on Android
 * @returns {boolean}
 */
export function isAndroid() {
  return /Android/.test(navigator.userAgent);
}

/**
 * Detects if the user is on mobile device
 * @returns {boolean}
 */
export function isMobile() {
  return isIOS() || isAndroid();
}

/**
 * Detects if the user is on desktop
 * @returns {boolean}
 */
export function isDesktop() {
  return !isMobile();
}

/**
 * Detects if the user came from Instagram
 * @returns {boolean}
 */
export function isFromInstagram() {
  const referrer = document.referrer;
  const userAgent = navigator.userAgent;
  
  // Check referrer for Instagram domains
  const instagramDomains = [
    'instagram.com',
    'www.instagram.com',
    'm.instagram.com',
    'l.instagram.com'
  ];
  
  // Check if referrer contains Instagram domain
  const isReferrerInstagram = instagramDomains.some(domain => 
    referrer.includes(domain)
  );
  
  // Check if user agent indicates Instagram in-app browser
  const isInstagramInApp = userAgent.includes('Instagram') || 
                          userAgent.includes('IG') ||
                          userAgent.includes('InstagramWebView');
  
  // Check URL parameters for Instagram indicators
  const urlParams = new URLSearchParams(window.location.search);
  const hasInstagramParams = urlParams.has('igshid') || 
                            urlParams.has('utm_source') && urlParams.get('utm_source').includes('instagram');
  
  return isReferrerInstagram || isInstagramInApp || hasInstagramParams;
}

/**
 * Detects if the user is in Instagram's in-app browser
 * @returns {boolean}
 */
export function isInstagramInAppBrowser() {
  const userAgent = navigator.userAgent;
  return userAgent.includes('Instagram') || 
         userAgent.includes('IG') ||
         userAgent.includes('InstagramWebView') ||
         userAgent.includes('InstagramWebViewer');
}

/**
 * Detects if the user is in any in-app browser
 * @returns {boolean}
 */
export function isInAppBrowser() {
  const userAgent = navigator.userAgent;
  return userAgent.includes('Instagram') || 
         userAgent.includes('IG') ||
         userAgent.includes('InstagramWebView') ||
         userAgent.includes('InstagramWebViewer') ||
         userAgent.includes('FBAN') ||
         userAgent.includes('FBAV') ||
         userAgent.includes('Twitter') ||
         userAgent.includes('Snapchat') ||
         userAgent.includes('TikTok') ||
         userAgent.includes('Line') ||
         userAgent.includes('WhatsApp') ||
         userAgent.includes('Telegram');
}

/**
 * Detects if we're already in an external browser (Safari/Chrome)
 * @returns {boolean}
 */
export function isExternalBrowser() {
  const userAgent = navigator.userAgent;
  
  // Check for Safari on iOS
  if (isIOS() && userAgent.includes('Safari') && !userAgent.includes('CriOS')) {
    return true;
  }
  
  // Check for Chrome on Android
  if (isAndroid() && userAgent.includes('Chrome') && !userAgent.includes('Instagram') && !userAgent.includes('FBAN')) {
    return true;
  }
  
  return false;
}

/**
 * Detects if coming from any social media platform
 * @returns {boolean}
 */
export function isFromSocialMedia() {
  const referrer = document.referrer;
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check for social media domains
  const socialDomains = [
    'instagram.com', 'www.instagram.com', 'm.instagram.com',
    'facebook.com', 'www.facebook.com', 'm.facebook.com',
    'twitter.com', 'mobile.twitter.com',
    'tiktok.com', 'www.tiktok.com',
    'snapchat.com', 'www.snapchat.com'
  ];
  
  const isSocialReferrer = socialDomains.some(domain => referrer.includes(domain));
  const hasSocialParams = urlParams.has('fbclid') || 
                         urlParams.has('igshid') || 
                         urlParams.has('utm_source') && 
                         (urlParams.get('utm_source').includes('instagram') || 
                          urlParams.get('utm_source').includes('facebook') ||
                          urlParams.get('utm_source').includes('twitter'));
  
  return isSocialReferrer || hasSocialParams || isInstagramInAppBrowser();
}

/**
 * Opens the current page in Safari/Chrome using deeplink
 * @param {Object} options - Additional options
 * @param {boolean} options.force - Force redirect even if not from social media
 * @param {string} options.fallbackUrl - Custom fallback URL
 * @param {number} options.delay - Delay before fallback (default: 1500ms)
 */
export function openPageInSafari(options = {}) {
  const { force = false, fallbackUrl = null, delay = 1500 } = options;
  
  // Only redirect if coming from social media or forced
  if (!force && !isFromSocialMedia()) {
    return;
  }
  
  // Only redirect on mobile devices
  if (!isMobile()) {
    return;
  }
  
  // Prevent infinite loop in any in-app browser
  if (isInAppBrowser()) {
    console.log('Deeplink: Skipping redirect in in-app browser to prevent infinite loop');
    return;
  }
  
  // Don't redirect if we're already in an external browser
  if (isExternalBrowser()) {
    console.log('Deeplink: Already in external browser, no redirect needed');
    return;
  }
  
  // Check if we've already attempted a redirect to prevent loops
  const hasRedirected = sessionStorage.getItem('deeplink_redirect_attempted');
  if (hasRedirected) {
    console.log('Deeplink: Redirect already attempted, skipping to prevent loop');
    return;
  }
  
  // Mark that we've attempted a redirect
  sessionStorage.setItem('deeplink_redirect_attempted', 'true');
  
  const currentUrl = window.location.href;
  const targetUrl = fallbackUrl || currentUrl;
  
  try {
    if (isIOS()) {
      // For iOS, redirect to Safari
      const safariUrl = `safari-https://${targetUrl.replace(/^https?:\/\//, '')}`;
      window.location.href = safariUrl;
      
      // Fallback after delay if Safari doesn't open
      setTimeout(() => {
        // Clear the flag before fallback
        sessionStorage.removeItem('deeplink_redirect_attempted');
        window.location.href = targetUrl;
      }, delay);
    } else if (isAndroid()) {
      // For Android, redirect to Chrome
      const chromeUrl = `googlechrome://navigate?url=${encodeURIComponent(targetUrl)}`;
      window.location.href = chromeUrl;
      
      // Fallback after delay if Chrome doesn't open
      setTimeout(() => {
        // Clear the flag before fallback
        sessionStorage.removeItem('deeplink_redirect_attempted');
        window.location.href = targetUrl;
      }, delay);
    }
  } catch (error) {
    console.error('Deeplink page redirect error:', error);
    
    // Clear the flag before fallback
    sessionStorage.removeItem('deeplink_redirect_attempted');
    
    // Fallback to regular redirect
    window.location.href = targetUrl;
  }
}

/**
 * Opens a URL using deeplink technology
 * @param {string} url - The URL to open
 * @param {Object} options - Additional options
 * @param {boolean} options.fallback - Whether to fallback to regular window.open
 * @param {string} options.target - Target for fallback (default: '_blank')
 */
export function openDeeplink(url, options = {}) {
  const { fallback = true, target = '_blank' } = options;
  
  // Clean the URL
  const cleanUrl = url.trim();
  
  if (!cleanUrl) {
    console.warn('Deeplink: No URL provided');
    return;
  }

  try {
    if (isIOS()) {
      // For iOS, try to open in Safari
      const safariUrl = `safari-https://${cleanUrl.replace(/^https?:\/\//, '')}`;
      window.location.href = safariUrl;
      
      // Fallback after a short delay if Safari doesn't open
      if (fallback) {
        setTimeout(() => {
          window.open(cleanUrl, target);
        }, 1000);
      }
    } else if (isAndroid()) {
      // For Android, try to open in Chrome
      const chromeUrl = `googlechrome://navigate?url=${encodeURIComponent(cleanUrl)}`;
      window.location.href = chromeUrl;
      
      // Fallback after a short delay if Chrome doesn't open
      if (fallback) {
        setTimeout(() => {
          window.open(cleanUrl, target);
        }, 1000);
      }
    } else {
      // For desktop, use regular window.open
      window.open(cleanUrl, target);
    }
  } catch (error) {
    console.error('Deeplink error:', error);
    
    // Fallback to regular window.open
    if (fallback) {
      window.open(cleanUrl, target);
    }
  }
}

/**
 * Creates a deeplink button with automatic detection
 * @param {string} url - The URL to open
 * @param {string} text - Button text
 * @param {Object} options - Additional options
 * @returns {HTMLElement} - Button element
 */
export function createDeeplinkButton(url, text, options = {}) {
  const button = document.createElement('a');
  button.href = '#';
  button.textContent = text;
  button.className = options.className || 'deeplink-button';
  
  button.addEventListener('click', (e) => {
    e.preventDefault();
    openDeeplink(url, options);
  });
  
  return button;
}

/**
 * Enhances all links with deeplink technology
 * @param {string} selector - CSS selector for links (default: 'a[data-deeplink]')
 */
export function enhanceLinks(selector = 'a[data-deeplink]') {
  const links = document.querySelectorAll(selector);
  
  links.forEach(link => {
    const url = link.getAttribute('href') || link.dataset.url;
    if (url) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        openDeeplink(url, {
          fallback: link.dataset.fallback !== 'false',
          target: link.dataset.target || '_blank'
        });
      });
    }
  });
}

/**
 * Auto-enhance links when DOM is ready
 */
export function autoEnhance() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceLinks());
  } else {
    enhanceLinks();
  }
}

/**
 * Initialize page deeplink redirect (immediate execution)
 * @param {Object} options - Options for page redirect
 */
export function initPageDeeplink(options = {}) {
  // Execute immediately for instant redirect
  if (isFromSocialMedia() && isMobile()) {
    // Add a small delay to ensure page is fully loaded
    setTimeout(() => {
      openPageInSafari(options);
    }, 100);
  }
}

/**
 * Initialize page deeplink redirect with delay
 * @param {Object} options - Options for page redirect
 * @param {number} delay - Delay before checking (default: 100ms)
 */
export function initPageDeeplinkDelayed(options = {}, delay = 100) {
  // Check if we should redirect the page
  if (isFromSocialMedia() && isMobile()) {
    // Small delay to ensure page is loaded
    setTimeout(() => {
      openPageInSafari(options);
    }, delay);
  }
}

/**
 * Safely opens the current page in external browser for Instagram users
 * This function is specifically designed to prevent infinite loops
 * @param {Object} options - Additional options
 * @param {string} options.fallbackUrl - Custom fallback URL
 * @param {number} options.delay - Delay before fallback (default: 1500ms)
 */
export function safeInstagramRedirect(options = {}) {
  const { fallbackUrl = null, delay = 1500 } = options;
  
  // Only work in Instagram in-app browser
  if (!isInstagramInAppBrowser()) {
    console.log('Deeplink: Not in Instagram in-app browser, skipping safe redirect');
    return;
  }
  
  // Check if we've already attempted a redirect
  const hasRedirected = sessionStorage.getItem('instagram_safe_redirect_attempted');
  if (hasRedirected) {
    console.log('Deeplink: Safe redirect already attempted, skipping');
    return;
  }
  
  // Mark that we've attempted a redirect
  sessionStorage.setItem('instagram_safe_redirect_attempted', 'true');
  
  const currentUrl = window.location.href;
  const targetUrl = fallbackUrl || currentUrl;
  
  try {
    if (isIOS()) {
      // For iOS, try to open in Safari
      const safariUrl = `safari-https://${targetUrl.replace(/^https?:\/\//, '')}`;
      window.location.href = safariUrl;
      
      // Fallback after delay if Safari doesn't open
      setTimeout(() => {
        // Clear the flag before fallback
        sessionStorage.removeItem('instagram_safe_redirect_attempted');
        window.location.href = targetUrl;
      }, delay);
    } else if (isAndroid()) {
      // For Android, try to open in Chrome
      const chromeUrl = `googlechrome://navigate?url=${encodeURIComponent(targetUrl)}`;
      window.location.href = chromeUrl;
      
      // Fallback after delay if Chrome doesn't open
      setTimeout(() => {
        // Clear the flag before fallback
        sessionStorage.removeItem('instagram_safe_redirect_attempted');
        window.location.href = targetUrl;
      }, delay);
    }
  } catch (error) {
    console.error('Deeplink safe Instagram redirect error:', error);
    
    // Clear the flag before fallback
    sessionStorage.removeItem('instagram_safe_redirect_attempted');
    
    // Fallback to regular redirect
    window.location.href = targetUrl;
  }
}

export default {
  isIOS,
  isAndroid,
  isMobile,
  isDesktop,
  isFromInstagram,
  isInstagramInAppBrowser,
  isInAppBrowser,
  isExternalBrowser,
  isFromSocialMedia,
  openPageInSafari,
  openDeeplink,
  createDeeplinkButton,
  enhanceLinks,
  autoEnhance,
  initPageDeeplink,
  initPageDeeplinkDelayed,
  safeInstagramRedirect
};
